import React from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Spinner from 'react-bootstrap/Spinner'
import { Typography } from '../styled'
import { useTheme } from '../../contexts/themeContext'
import { createColors } from '../buttons/CoreButton'

const sizeMap = {
  sm: '120px',
  base: '160px',
  md: '200px',
  lg: '280px',
  full: '100%',
}

// --- Variant styles still pure functions
const variantStyles = (colors) => ({
  solid: (color) => ({
    backgroundColor: color.main,
    color: color.text,
    '&:hover:not(:disabled)': {
      backgroundColor: color.hover,
      color: color.text,
    },
    '&:disabled': {
      backgroundColor: color.disabled.background,
      color: color.disabled.text,
      cursor: 'not-allowed',
    },
  }),
  outlined: (color) => ({
    backgroundColor: 'transparent',
    border: `1px solid ${color.main}`,
    color: color.main,
    '&:hover:not(:disabled)': {
      backgroundColor: color.main,
      color: color.text,
    },
    '&:disabled': {
      borderColor: color.disabled.background,
      color: color.disabled.background,
      cursor: 'not-allowed',
      backgroundColor: 'transparent',
    },
  }),
  ghost: (color) => ({
    backgroundColor: 'transparent',
    color: color.main,
    '&:hover:not(:disabled)': {
      backgroundColor: colors.surface.light,
      color: color.hover,
    },
    '&:disabled': {
      color: color.disabled.background,
      cursor: 'not-allowed',
    },
  }),
  link: (color) => ({
    backgroundColor: 'transparent',
    color: color.main,
    textDecoration: 'underline',
    '&:hover:not(:disabled)': {
      textDecoration: 'none',
      color: color.hover,
    },
    '&:disabled': {
      color: color.disabled.background,
      cursor: 'not-allowed',
    },
  }),
})

// --- Styled Dropdown toggle (theme is passed via props automatically)
const DropdownToggle = styled(Dropdown.Toggle)(
  ({ theme, variant = 'solid-primary', size = 'base', arrow, iconOnly }) => {
    const colors = createColors(theme)
    const [type, colorName = 'primary'] = variant.split('-')
    const colorConfig = colors[colorName] || colors.primary
    const styles = variantStyles(colors)

    return {
      height: '36px',
      borderRadius: '2px !important',
      padding: variant.startsWith('link') ? '0' : iconOnly ? '0 10px' : '0 16px',
      width: variant.startsWith('link')
        ? 'auto'
        : iconOnly
        ? '36px'
        : sizeMap[size],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '12px !important',
      fontWeight: 500,
      transition: 'all 0.2s ease-in-out',
      border: 'none',
      cursor: 'pointer',
      '&:focus': { outline: 'none' },
      '&.no-arrow-dropdown-toggle::after': {
        display: 'none',
      },
      ...styles[type]?.(colorConfig),
    }
  },
)

// --- Component
function BaseDropdown({
  text,
  options,
  label,
  disabled,
  clickHandler,
  isLoading = false,
  arrow = true,
  iconOnly = false,
  variant = 'solid-primary',
  size = 'base',
  className = '',
}) {
  const { theme } = useTheme()
  const colors = createColors(theme)

  const [variantType, colorName = 'primary'] = variant.split('-')
  const colorConfig = colors[colorName] || colors.primary

  const getSpinnerVariant = () => {
    if (disabled) {
      return variantType === 'solid' ? 'light' : colorConfig.disabled.background
    }
    return variantType === 'solid' ? 'light' : colorName
  }

  return (
    <Dropdown>
      <DropdownToggle
        disabled={disabled || isLoading}
        id="dropdown-basic"
        variant={variant}
        size={size}
        arrow={arrow}
        iconOnly={iconOnly}
        className={[
          'py-1',
          !arrow && 'no-arrow-dropdown-toggle',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isLoading && (
          <Spinner
            animation="border"
            variant={getSpinnerVariant()}
            size="sm"
            className="me-2"
          />
        )}
        <Typography size="12px">{text}</Typography>
      </DropdownToggle>

      <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'auto' }}>
        {options
          .filter((option) => !option.hide)
          .map((option, index) => (
            <Dropdown.Item
              key={index}
              className="text-capitalize"
              onClick={() => clickHandler(option, index)}
              disabled={option.isDisabled || isLoading}
            >
              {option.isLoading && (
                <Spinner
                  animation="border"
                  variant={getSpinnerVariant()}
                  size="sm"
                  className="me-2"
                />
              )}
              <Typography size="13px" regular>
                {option?.icon} {option[label]}
              </Typography>
            </Dropdown.Item>
          ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}

BaseDropdown.propTypes = {
  text: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  clickHandler: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  arrow: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'base', 'md', 'lg', 'full']),
  className: PropTypes.string,
}

export default BaseDropdown
